import { useEffect, useState } from 'react';
import apiClient from '@/lib/api/client';

// ── Claude status toast ─────────────────────────────────────────────────────
// Floating indicator on the dev dash showing what Claude is working on in the
// change-request queue. Collapsible to a status dot: working / idle / error
// (a request stalled in-progress > 24h) / inactive (no activity in 7 days).
// Working state shows completed-today / total-requests-today.

interface CR {
  id: number;
  title: string;
  page: string;
  status: string;
  created_at: string;
  fulfilled_at?: string | null;
}

type Mood = 'working' | 'idle' | 'error' | 'inactive';

const MOOD_META: Record<Mood, { icon: string; cls: string; label: string; spin?: boolean }> = {
  working:  { icon: 'smart_toy',    cls: 'bg-[#C77D3F] text-white', label: 'Working', spin: false },
  idle:     { icon: 'check_circle', cls: 'bg-green-600 text-white', label: 'Idle — queue clear' },
  error:    { icon: 'error',        cls: 'bg-red-600 text-white',   label: 'Stalled — check queue' },
  inactive: { icon: 'bedtime',      cls: 'bg-gray-400 text-white',  label: 'Inactive' },
};

export function ClaudeStatusToast() {
  const [reqs, setReqs] = useState<CR[]>([]);
  const [live, setLive] = useState<{ text: string; at: string } | null>(null);
  const [paused, setPaused] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('claudeToastCollapsed') === '1');

  useEffect(() => {
    const load = () => {
      apiClient.get('/dev/change-requests').then(r => setReqs(r.data.data ?? [])).catch(() => {});
      apiClient.get('/dev/queue-state').then(r => {
        const d = r.data.data;
        setPaused(!!d?.paused);
        setLive(d?.status_text && d?.status_at ? { text: d.status_text, at: d.status_at } : null);
      }).catch(() => {});
    };
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, []);

  const now = Date.now();
  const today = new Date().toISOString().slice(0, 10);
  const wip = reqs.filter(r => r.status === 'in_progress');
  const open = reqs.filter(r => r.status === 'open');
  const doneToday = reqs.filter(r => r.status === 'done' && (r.fulfilled_at || '').slice(0, 10) === today).length;
  const createdToday = reqs.filter(r => (r.created_at || '').slice(0, 10) === today).length;
  const stalled = wip.some(r => now - new Date(r.created_at).getTime() > 24 * 3600 * 1000);
  const recentDone = reqs.some(r => r.fulfilled_at && now - new Date(r.fulfilled_at).getTime() < 7 * 24 * 3600 * 1000);
  // Live heartbeat: Claude reports its current activity as it works; fresh
  // (≤15 min) beats queue inference.
  const liveFresh = live && now - new Date(live.at).getTime() < 15 * 60 * 1000;
  const liveAgo = live ? Math.max(0, Math.round((now - new Date(live.at).getTime()) / 60000)) : 0;

  const mood: Mood = stalled ? 'error'
    : (liveFresh || wip.length > 0 || open.length > 0) ? 'working'
    : recentDone ? 'idle'
    : 'inactive';
  const meta = MOOD_META[mood];

  const toggle = () => {
    setCollapsed(c => {
      localStorage.setItem('claudeToastCollapsed', c ? '0' : '1');
      return !c;
    });
  };

  if (collapsed) {
    return (
      <button
        onClick={toggle}
        title={`Claude: ${meta.label}${mood === 'working' ? ` · ${doneToday}/${createdToday} today` : ''}`}
        className={`fixed bottom-4 right-4 z-40 h-11 w-11 rounded-full shadow-lg flex items-center justify-center ${meta.cls} active:scale-95 transition-transform`}
      >
        <span className={`material-symbols-outlined text-xl ${mood === 'working' ? 'animate-pulse' : ''}`}>{meta.icon}</span>
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-40 w-72 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
      <div className={`flex items-center gap-2 px-3 py-2 ${meta.cls}`}>
        <span className={`material-symbols-outlined text-lg ${mood === 'working' ? 'animate-pulse' : ''}`}>{meta.icon}</span>
        <span className="text-sm font-bold flex-1">Claude · {meta.label}</span>
        {mood === 'working' && (
          <span className="text-[11px] font-bold bg-white/20 rounded-full px-2 py-0.5">{doneToday}/{createdToday} today</span>
        )}
        <button onClick={toggle} title="Collapse to indicator" className="opacity-80 hover:opacity-100">
          <span className="material-symbols-outlined text-lg">remove</span>
        </button>
      </div>
      <div className="p-3 space-y-1.5 max-h-56 overflow-y-auto">
        {paused && (
          <p className="text-[11px] font-bold text-red-700 bg-red-50 border border-red-100 rounded-lg px-2 py-1">Queue fulfilment is paused — flip "Queue active" to resume.</p>
        )}
        {liveFresh && (
          <div className="bg-[#FAF6F0] border border-[#C77D3F]/25 rounded-lg px-2.5 py-2">
            <p className="text-[10px] font-bold uppercase tracking-wide text-[#8a5526]">Right now</p>
            <p className="text-xs text-gray-800 leading-snug">{live!.text}</p>
            <p className="text-[9px] text-gray-400 mt-0.5">{liveAgo === 0 ? 'just now' : `${liveAgo} min ago`}</p>
          </div>
        )}
        {wip.length > 0 && (
          <>
            <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400">Working on now</p>
            {wip.map(r => (
              <div key={r.id} className="flex items-start gap-1.5">
                <span className="material-symbols-outlined text-sm text-blue-500 animate-spin mt-0.5">progress_activity</span>
                <p className="text-xs text-gray-800 leading-snug">{r.title} <span className="text-gray-400">· {r.page}</span></p>
              </div>
            ))}
          </>
        )}
        {open.length > 0 && (
          <p className="text-[11px] text-amber-700">{open.length} more request{open.length !== 1 ? 's' : ''} waiting in the queue.</p>
        )}
        {mood === 'idle' && <p className="text-xs text-gray-500">Nothing queued — {doneToday > 0 ? `${doneToday} fulfilled today.` : 'add a request below and it gets picked up on the daily check.'}</p>}
        {mood === 'error' && <p className="text-xs text-red-600">A request has been in progress over 24h — it may need attention.</p>}
        {mood === 'inactive' && <p className="text-xs text-gray-400">No queue activity this week.</p>}
      </div>
    </div>
  );
}
